import { observable, computed, action } from 'mobx';
import Alert from 'react-s-alert';

import { App, AppRegistry, ArrayApp, ModelRegistry, Input } from 'core/index';
import { getHostName } from 'core/url';

import { PermissionedModel } from 'apps/permissions/app';
import { TagModel, TagsApp } from 'apps/tag/app';
import { FormStore } from 'libs/forms/app';
import { clientStore } from 'apps/client/app';
import { modalStore } from 'apps/modals/app';
import SignInModal from 'apps/signIn/main/modal';
import { raiseUserError } from 'apps/utils';

import { conf } from 'apps/aggregator/config.yaml';

import i18next from 'i18next';

export class PostModel extends PermissionedModel {
  static registryName = 'PostModel';
  static ModelProps = {
    tags: [TagModel],
    creatorProfile: 'ProfileModel',
    commenters: ['ProfileModel'],
  };

  @observable slug = '';
  @observable title = '';
  @observable msg = '';
  @observable url = '';
  @observable tags = [];
  @observable creatorProfile = null;
  @observable dateCreated = '';
  @observable lastUpdated = '';
  @observable isUpvoted = false;
  @observable upvotes = null;
  @observable upvoteCount = 1;
  @observable comments = null;
  @observable commenters = [];
  @computed get commentsCount() {
    if (this.comments) {
      return this.comments.totalCount;
    }
    return 0;
  }
}
ModelRegistry.add(PostModel);

export class PostApp extends App {
  static registryName = 'PostApp';
  static AppProps = {
    tags: TagsApp,
    creatorProfile: 'ProfileApp',
    commenters: 'ProfilesApp',
  };

  static Model = PostModel;
  @observable editing = false;
  @observable upvote;
  @observable undoIsHovered = false;
  createForm() {
    this.form = new FormStore(this, conf, 'postForm');
  }

  @computed get displayDownVote() {
    if (this.isUpvoted && this.undoIsHovered) {
      return true;
    }
    return false;
  }

  @computed get isQuestion() {
    if (this.title.startsWith('Q:') || this.title.endsWith('?')) {
      return true;
    }
    return false;
  }

  @computed get origin() {
    return getHostName(this.url);
  }

  @computed get isOwnPost() {
    if (
      clientStore.signedInProfile &&
      this.creatorProfile.username === clientStore.signedInProfile.username
    ) {
      return true;
    }
    return false;
  }

  @action EditPost = () => {
    this.editFormStore = new FormStore(this, conf, 'editPost');
    this.editing = true;
    clientStore.context.set('postApp', this);
  };

  @action Cancel = () => {
    this.ResetLocals();
    this.editFormStore = null;
    this.editing = false;
    clientStore.context.delete('postApp');
  };

  @action Save = () => {
    if (this.editFormStore.isValid) {
      const input = new Input({
        id: this.id,
        title: this.title,
        msg: this.msg,
      });
      this.manager.UpdatePost(input, {
        onSysErr: this.onSaveErr,
        onUserErr: this.onSaveErr,
        onSuccess: this.onSaveSuccess,
      });
    } else {
      this.editFormStore.alert();
    }
  };

  @action onSaveErr = error => {
    this.ResetLocals();
    raiseUserError(error, 'save', 'Post');
  };

  @action onSaveSuccess = () => {
    this.editing = false;
    this.ResetLocals();
    Alert.success('Your Post has been updated successfully.');
    clientStore.context.delete('postApp');
  };

  @action Upvote = () => {
    if (clientStore.signedInProfile === null) {
      modalStore.SetModalComponent(SignInModal);
    } else {
      this.isUpvoted = true;
      this.upvoteCount += 1;
      const upvoteInput = new Input({
        id: this.id,
        upvote: true,
      });
      this.manager.UpdatePost(upvoteInput, {
        onSysErr: this.onUpvoteErr,
        onUserErr: this.onUpvoteErr,
        onSuccess: this.onUpvoteSuccess,
      });
    }
  };

  @action onUpvoteErr = error => {
    this.ResetLocals();
    raiseUserError(error, 'upvote', 'Post');
  };

  @action onUpvoteSuccess = () => {
    this.ResetLocals();
  };

  @action SetUndoIsHovered = bool => {
    this.undoIsHovered = bool;
  };

  @action Downvote = () => {
    this.isUpvoted = true;
    this.upvote = false;
    this.undoIsHovered = false;
    this.upvoteCount -= 1;
    const upvoteInput = new Input({
      id: this.id,
      upvote: false,
    });
    this.manager.UpdatePost(upvoteInput, {
      onSysErr: this.onDownvoteErr,
      onUserErr: this.onDownvoteErr,
      onSuccess: this.onDownvoteSuccess,
    });
  };

  @action onDownvoteErr = error => {
    this.ResetLocals();
    raiseUserError(error, 'downvote', 'Post');
  };

  @action onDownvoteSuccess = () => {
    this.ResetLocals();
  };

  @action Submit = () => {
    if (this.form.isValid) {
      this.form.SetPosting(true);
      clientStore.SetPosting(true);
      this.manager.Submit({
        onSysErr: this.onSubmitSysErr,
        onUserErr: this.onSubmitUserErr,
        onSuccess: this.onSubmitSuccess,
      });
    } else {
      this.form.alert();
    }
  };

  @action onSubmitSysErr = error => {
    this.form.SetPosting(false);
    clientStore.SetPosting(false);
    raiseUserError(error, 'submit', 'Post');
  };

  @action onSubmitUserErr = () => {
    this.form.SetPosting(false);
    clientStore.SetPosting(false);
  };

  @action onSubmitSuccess = result => {
    this.form.SetPosting(false);
    this.form.clearDraft();
    clientStore.SetPosting(false);
    modalStore.ClearComponent();
    clientStore.routerStore.navigate('home.post', {
      slug: result.slug,
    });
    const channelNames = [];
    this.tags.apps.forEach(tag => {
      channelNames.push(`#${tag.name}`);
    });
    if (channelNames.length === 1) {
      setTimeout(() => {
        const channelName = channelNames[0];
        Alert.success(
          i18next.t('Posted successfully into {{channelName}}', {
            channelName,
          }),
        );
      }, 300);
    } else {
      const channelName = channelNames[0];
      setTimeout(() => {
        Alert.success(
          i18next.t('Posted successfully into {{channelName}}', {
            channelName,
          }),
        );
      }, 300);
    }
  };

  @action Delete = () => {
    const input = new Input({
      id: this.id,
      deleted: true,
    });
    this.manager.UpdatePost(input, {
      onSysErr: this.onDeleteErr,
      onUserErr: this.onDeleteErr,
      onSuccess: this.onDeleteSuccess,
    });
  };

  @action onDeleteErr = error => {
    this.ResetLocals();
    raiseUserError(error, 'delete', 'Post');
  };

  @action onDeleteSuccess = () => {
    this.ResetLocals();
    modalStore.ClearComponent();
    if (this.manager.constructor.registryName === 'PostPageAppManager') {
      clientStore.routerStore.navigate('home');
    } else if (this.manager.constructor.registryName === 'PostConnManager') {
      this.manager.Clear();
      this.manager.loadMore();
    }
    Alert.success('Post has been deleted successfully.');
  };
}
AppRegistry.add(PostApp);

export class PostCardLayout {
  @computed get layoutSpec() {
    return { cardColumns: 1, initialRows: 10, extraRows: 10 };
  }

  @computed get initialBatchSize() {
    return this.layoutSpec.initialRows * this.layoutSpec.cardColumns;
  }

  @computed get batchSize() {
    return this.layoutSpec.extraRows * this.layoutSpec.cardColumns;
  }
}

export class PostArrayApp extends ArrayApp {
  static registryName = 'PostArrayApp';
  static ElementApp = PostApp;
  cardLayout = new PostCardLayout();
}
AppRegistry.add(PostArrayApp);
